import { useLayoutEffect, useRef, useState } from 'react'
import { Box, Button, Group, Text, Tooltip } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { TbChevronLeft, TbChevronRight } from 'react-icons/tb'

import type { DocPoint } from './features'
import TooltipIconButton from './TooltipIconButton'
import { copyToCanvas, useFittedSize } from './useFittedCanvas'

const LightboxImage = ({
  image,
  preview,
  actualSize,
  hidden,
}: {
  image: HTMLCanvasElement | null
  preview: boolean
  actualSize: boolean
  hidden: boolean
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const [drawn, setDrawn] = useState<HTMLCanvasElement | null>(null)
  const fitted = useFittedSize(drawn, frameRef)
  const size = actualSize ? drawn : fitted

  useLayoutEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !image || (preview && actualSize)) return
    copyToCanvas(canvas, image)
    setDrawn(image)
  }, [actualSize, image, preview])

  return (
    <div
      ref={frameRef}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: actualSize ? 'flex-start' : 'center',
        justifyContent: actualSize ? 'flex-start' : 'center',
      }}
    >
      <canvas
        ref={canvasRef}
        data-testid={drawn && !hidden ? 'documentation-lightbox-image' : undefined}
        style={{
          visibility: drawn && !hidden ? 'visible' : 'hidden',
          width: size ? `${size.width}px` : undefined,
          height: size ? `${size.height}px` : undefined,
        }}
      />
    </div>
  )
}

const caption = (point: DocPoint | null) =>
  point
    ? [point.time != null ? new Date(point.time).toLocaleTimeString() : '', point.props().street, point.imagePath]
        .filter(Boolean)
        .join(' · ')
    : ''

const Lightbox = ({
  point,
  image,
  preview,
  error,
  prevDisabled,
  nextDisabled,
  onPrev,
  onNext,
  onClose,
}: {
  point: DocPoint | null
  image: HTMLCanvasElement | null
  preview: HTMLCanvasElement | null
  error: string | null
  prevDisabled: boolean
  nextDisabled: boolean
  onPrev: () => void
  onNext: () => void
  onClose: () => void
}) => {
  const { t } = useTranslation('documentation')
  const [actualSize, setActualSize] = useState(false)
  const sizeLabel = actualSize ? t('fit') : t('actualSize')

  return (
    <Box
      pos="fixed"
      inset={0}
      style={{ zIndex: 400, background: 'rgb(6, 8, 12)', overflow: actualSize ? 'auto' : 'hidden' }}
      onClick={event => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <Group pos="absolute" top={16} right={16} gap="sm" style={{ zIndex: 1 }}>
        <TooltipIconButton label={t('previous')} disabled={prevDisabled} onClick={onPrev}>
          <TbChevronLeft size={18} />
        </TooltipIconButton>
        <TooltipIconButton label={t('next')} disabled={nextDisabled} onClick={onNext}>
          <TbChevronRight size={18} />
        </TooltipIconButton>
        <Tooltip label={sizeLabel} withArrow>
          <Button size="sm" variant="default" onClick={() => setActualSize(value => !value)}>
            {sizeLabel}
          </Button>
        </Tooltip>
        <Tooltip label={t('close')} withArrow>
          <Button size="sm" variant="default" onClick={onClose}>
            {t('close')}
          </Button>
        </Tooltip>
      </Group>
      <LightboxImage image={image ?? preview} preview={!image} actualSize={actualSize} hidden={Boolean(error)} />
      {error ? (
        <Text
          pos="absolute"
          top="50%"
          left="50%"
          size="md"
          c="gray.4"
          ta="center"
          style={{ transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}
        >
          {error}
        </Text>
      ) : null}
      <Text pos="absolute" bottom={20} left={24} size="sm" c="dimmed" ff="monospace">
        {caption(point)}
      </Text>
    </Box>
  )
}

export default Lightbox
